import React, { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getEquipment, getCategories, EquipmentStatus, checkEquipmentAvailability, getEquipmentBlockedDates, getUserDiscount } from '../services/supabase';
import 'react-datepicker/dist/react-datepicker.css';
import { EquipmentCard } from '../components/equipment/EquipmentCard';
import { Button } from '../components/ui/Button';
import DateRangeSelector from '../components/booking/DateRangeSelector';
import BookingConfirmation from '../components/booking/BookingConfirmation';
import Dropdown from '../components/ui/Dropdown';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';
import { getCurrentUser } from '../services/telegram-auth';

// Функция для склонения слова "день" в зависимости от числа
export const getDaysText = (days: number): string => {
    const lastDigit = days % 10;
    const lastTwoDigits = days % 100;

    if (lastTwoDigits >= 11 && lastTwoDigits <= 19) {
        return 'дней';
    }

    if (lastDigit === 1) {
        return 'день';
    }

    if (lastDigit >= 2 && lastDigit <= 4) {
        return 'дня';
    }

    return 'дней';
};

// Интерфейс для данных оборудования
interface Equipment {
    id: string;
    name: string;
    description: string | null;
    image_url: string | null;
    daily_rate: number;
    category_id: string;
    status: EquipmentStatus;
    quantity: number;
    available_quantity?: number;
    is_available?: boolean;
    sort_order?: number | null;
}

// Интерфейс для категорий
interface Category {
    id: string;
    name: string;
    description: string | null;
    image_url: string | null;
}

// Интерфейс для выбранного оборудования
interface SelectedEquipment {
    id: string;
    name: string;
    quantity: number;
    dailyRate: number;
    imageUrl?: string;
    category_id: string;
    status: string;
    is_available?: boolean;
    available_quantity?: number;
    description?: string | null;
    sort_order?: number | null;
}

// Интерфейс для модального окна выбора количества
interface QuantityModalProps {
    equipment: Equipment | null;
    onConfirm: (id: string, quantity: number) => void;
    onCancel: () => void;
}

// Компонент модального окна для выбора количества
const QuantityModal: React.FC<QuantityModalProps> = ({ equipment, onConfirm, onCancel }) => {
    const [quantity, setQuantity] = useState(1);
    const [inputValue, setInputValue] = useState('1');

    // Убедимся, что оборудование передано и доступно
    if (!equipment) return null;

    // Максимальное доступное количество (с проверкой на undefined)
    const maxQuantity = equipment.available_quantity !== undefined
        ? equipment.available_quantity
        : (equipment.quantity || 1);

    // Защита от undefined в daily_rate
    const dailyRate: number = equipment.daily_rate !== undefined
        ? equipment.daily_rate
        : ('dailyRate' in equipment ? (equipment as any).dailyRate : 0);

    // Обработчик изменения количества
    const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;
        setInputValue(value);

        const numValue = parseInt(value, 10);
        if (!isNaN(numValue)) {
            setQuantity(numValue);
        }
    };

    // Обработчик потери фокуса - корректируем значение автоматически
    const handleBlur = () => {
        let correctedValue = quantity;
        let needsCorrection = false;

        // Если значение меньше 1 или не является числом
        if (isNaN(quantity) || quantity < 1) {
            correctedValue = 1;
            needsCorrection = true;
        }
        // Если значение больше максимального
        else if (quantity > maxQuantity) {
            correctedValue = maxQuantity;
            needsCorrection = true;
        }

        // Если необходима коррекция, обновляем оба состояния
        if (needsCorrection) {
            setQuantity(correctedValue);
            setInputValue(correctedValue.toString());
        }
    };

    const handleButtonClick = (newValue: number) => {
        setQuantity(newValue);
        setInputValue(newValue.toString());
    };

    const handleConfirm = () => {
        let finalQuantity = quantity;

        // Если значение меньше 1, устанавливаем минимальное значение
        if (isNaN(quantity) || quantity < 1) {
            finalQuantity = 1;
        }
        // Если значение больше максимального, устанавливаем максимальное значение
        else if (quantity > maxQuantity) {
            finalQuantity = maxQuantity;
        }

        onConfirm(equipment.id, finalQuantity);
    };

    // Вычисляем реальное количество и стоимость для отображения
    const displayQuantity = isNaN(quantity) || quantity < 1 ? 1 :
        quantity > maxQuantity ? maxQuantity : quantity;
    const totalPrice = dailyRate * displayQuantity;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-4 rounded-lg shadow-lg w-full max-w-sm">
                <h3 className="text-base font-semibold mb-2">Выберите количество</h3>
                <p className="mb-3 text-gray-600 text-sm">
                    {equipment.name} (доступно: {maxQuantity} шт.)
                </p>

                <div className="mb-4">
                    <label htmlFor="quantity" className="block text-sm font-medium text-gray-700 mb-1">
                        Количество:
                    </label>
                    <div className="flex items-center">
                        <button
                            type="button"
                            onClick={() => handleButtonClick(Math.max(1, quantity - 1))}
                            className="px-3 py-1 border border-gray-300 rounded-l-md bg-gray-50 hover:bg-gray-100"
                            disabled={quantity <= 1}
                        >
                            -
                        </button>
                        <input
                            type="text"
                            id="quantity"
                            value={inputValue}
                            onChange={handleQuantityChange}
                            onBlur={handleBlur}
                            className="w-full px-3 py-1 border-t border-b border-gray-300 text-center bg-white text-gray-800"
                            min="1"
                            max={maxQuantity}
                        />
                        <button
                            type="button"
                            onClick={() => handleButtonClick(Math.min(maxQuantity, quantity + 1))}
                            className="px-3 py-1 border border-gray-300 rounded-r-md bg-gray-50 hover:bg-gray-100"
                            disabled={quantity >= maxQuantity}
                        >
                            +
                        </button>
                    </div>
                </div>

                <div className="mb-4 bg-gray-50 p-3 rounded-md">
                    <div className="flex justify-between">
                        <span className="text-sm text-gray-600">Цена за единицу:</span>
                        <span className="font-medium">{dailyRate.toLocaleString()} ₽/день</span>
                    </div>
                    <div className="flex justify-between mt-1">
                        <span className="text-sm text-gray-600">Итого:</span>
                        <span className="font-medium">{totalPrice.toLocaleString()} ₽/день</span>
                    </div>
                </div>

                <div className="flex justify-end space-x-2">
                    <Button
                        variant="outline"
                        onClick={onCancel}
                    >
                        Отмена
                    </Button>
                    <Button
                        onClick={handleConfirm}
                    >
                        Подтвердить
                    </Button>
                </div>
            </div>
        </div>
    );
};

const EquipmentPage = () => {
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [availableEquipment, setAvailableEquipment] = useState<SelectedEquipment[]>([]);
    const [selectedEquipment, setSelectedEquipment] = useState<SelectedEquipment[]>([]);
    const [showBookingWidget, setShowBookingWidget] = useState(false);
    const [showBookingConfirmation, setShowBookingConfirmation] = useState(false);
    const [selectedStartDate, setSelectedStartDate] = useState<Date | null>(null);
    const [selectedEndDate, setSelectedEndDate] = useState<Date | null>(null);
    const [showQuantityModal, setShowQuantityModal] = useState<{
        show: boolean;
        equipment: Equipment | null;
    }>({ show: false, equipment: null });
    const [highlightDateSelector, setHighlightDateSelector] = useState(false);
    const [calendarFocusedEquipmentId, setCalendarFocusedEquipmentId] = useState<string | null>(null);
    const [showCalendar, setShowCalendar] = useState(false);
    const [disabledDates, setDisabledDates] = useState<Date[]>([]);
    const [userDiscount, setUserDiscount] = useState<number>(0);
    const [applyDiscount, setApplyDiscount] = useState<boolean>(true);
    const navigate = useNavigate();

    // Получаем список оборудования через React Query
    const { data: equipment = [], isLoading: isLoadingEquipment, isError: isErrorEquipment } = useQuery<Equipment[]>({
        queryKey: ['equipment'],
        queryFn: async () => {
            const { data, error } = await getEquipment();
            if (error) throw new Error(error.message);
            return data as Equipment[] || [];
        },
    });

    // Запрос на получение категорий
    const { data: categoriesData = [], isLoading: isLoadingCategories } = useQuery<Category[]>({
        queryKey: ['categories'],
        queryFn: async () => {
            const { data, error } = await getCategories();
            if (error) throw error;
            return data as Category[] || [];
        },
    });

    // Запрос данных о доступности оборудования при выборе дат
    const { data: availabilityData, isLoading: isLoadingAvailability } = useQuery({
        queryKey: ['equipmentAvailability', { startDate: selectedStartDate, endDate: selectedEndDate }],
        queryFn: async () => {
            // Проверяем, выбраны ли даты
            if (!selectedStartDate || !selectedEndDate) {
                console.log('Даты не выбраны, используем все оборудование');
                return equipment; // Возвращаем все оборудование, если даты не выбраны
            }

            console.log('Запрос доступности с датами:', selectedStartDate, selectedEndDate);

            try {
                // Запрашиваем доступность оборудования на выбранные даты
                const { data, error } = await checkEquipmentAvailability(selectedStartDate, selectedEndDate);

                if (error) {
                    console.error('Ошибка при проверке доступности:', error);
                    return equipment; // В случае ошибки возвращаем все оборудование
                }

                if (!data || !Array.isArray(data) || data.length === 0) {
                    console.log('Нет данных о доступности, используем все оборудование:', equipment?.length || 0, 'items');
                    return equipment; // Если нет данных о доступности, возвращаем все оборудование
                }

                console.log('Получены данные о доступности (массив):', data.length, 'элементов');
                return data;
            } catch (error) {
                console.error('Непредвиденная ошибка при получении доступности:', error);
                return equipment; // В случае ошибки возвращаем все оборудование
            }
        },
        enabled: !!equipment && !!selectedStartDate && !!selectedEndDate,
        staleTime: 1000 * 60 * 5 // Кэшируем на 5 минут
    });

    // Получаем информацию о скидке пользователя при монтировании компонента
    useEffect(() => {
        const fetchUserDiscount = async () => {
            const currentUser = getCurrentUser();
            if (currentUser) {
                try {
                    const { data, error } = await getUserDiscount();
                    if (error) {
                        console.error('Ошибка при получении скидки:', error);
                    } else if (data !== undefined) {
                        setUserDiscount(data);
                        console.log('Скидка пользователя:', data);
                    }
                } catch (error) {
                    console.error('Ошибка при получении скидки:', error);
                }
            }
        };
        
        fetchUserDiscount();
    }, []);

    // Обновляем доступное оборудование при изменении результатов запроса доступности
    useEffect(() => {
        if (availabilityData) {
            console.log('Получены данные о доступности оборудования:', availabilityData);

            // Проверяем, есть ли поле data в availabilityData 
            const equipmentArray = typeof availabilityData === 'object' && 'data' in availabilityData && Array.isArray(availabilityData.data)
                ? availabilityData.data
                : availabilityData;

            // Проверяем статусы оборудования
            if (Array.isArray(equipmentArray)) {
                // Находим соответствующее оборудование из основного списка
                const mappedEquipment = equipment.map(originalItem => {
                    // Ищем информацию о доступности для этого оборудования
                    const availabilityInfo = equipmentArray.find(item => item.id === originalItem.id);

                    return {
                        id: String(originalItem.id),
                        name: originalItem.name,
                        quantity: originalItem.quantity,
                        dailyRate: originalItem.daily_rate,
                        imageUrl: originalItem.image_url || undefined,
                        category_id: originalItem.category_id,
                        status: availabilityInfo ?
                            (availabilityInfo.is_available ? 'available' : 'booked') :
                            originalItem.status,
                        is_available: availabilityInfo ? availabilityInfo.is_available : true,
                        available_quantity: availabilityInfo ? availabilityInfo.available_quantity : originalItem.quantity,
                        description: originalItem.description,
                        sort_order: originalItem.sort_order
                    };
                });

                setAvailableEquipment(mappedEquipment);
                console.log('Available equipment updated:', mappedEquipment.length, 'items');
            } else {
                console.error('Некорректный формат данных о доступности:', equipmentArray);
                // Если данные некорректны, используем оригинальный список оборудования
                setAvailableEquipment(equipment.map(item => ({
                    id: String(item.id),
                    name: item.name,
                    quantity: item.quantity,
                    dailyRate: item.daily_rate,
                    imageUrl: item.image_url || undefined,
                    category_id: item.category_id,
                    status: item.status,
                    is_available: true,
                    available_quantity: item.quantity,
                    description: item.description,
                    sort_order: item.sort_order
                })));
            }
        } else if (equipment && equipment.length > 0) {
            console.log('No availability data, using all equipment:', equipment.length, 'items');
            setAvailableEquipment(equipment.map(item => ({
                id: String(item.id),
                name: item.name,
                quantity: item.quantity,
                dailyRate: item.daily_rate,
                imageUrl: item.image_url || undefined,
                category_id: item.category_id,
                status: item.status,
                is_available: true,
                available_quantity: item.quantity,
                description: item.description,
                sort_order: item.sort_order
            })));
        }
    }, [availabilityData, equipment]);

    // Общее состояние загрузки и ошибки
    const isLoadingCombined = isLoadingEquipment || isLoadingCategories || isLoadingAvailability;
    const isError = isErrorEquipment;

    // Обработчик изменения дат
    const handleDatesChange = (startDate: Date | null, endDate: Date | null) => {
        console.log('handleDatesChange:', { startDate, endDate });
        setSelectedStartDate(startDate);
        setSelectedEndDate(endDate);

        // Сбрасываем подсветку, если она была активирована
        if (startDate && endDate) {
            setHighlightDateSelector(false);
            setShowCalendar(false); // Скрываем календарь после выбора дат

            // Если был фокус на конкретном оборудовании, и даты выбраны, автоматически выбираем это оборудование
            if (calendarFocusedEquipmentId) {
                const equip = availableEquipment.find(e => e.id === calendarFocusedEquipmentId);
                if (equip) {
                    const maxQuantity = equip.available_quantity !== undefined
                        ? equip.available_quantity
                        : (equip.quantity || 0);

                    if (maxQuantity === 1) {
                        // Если доступна только 1 штука, сразу добавляем без модального окна
                        handleEquipmentSelect(equip, 1);
                    } else {
                        // Если больше 1, показываем модальное окно выбора количества
                        setShowQuantityModal({ show: true, equipment: equip as unknown as Equipment });
                    }
                }
                // Сбрасываем фокус на оборудовании
                setCalendarFocusedEquipmentId(null);
            }
        }
    };

    // Фильтрация оборудования по категории, поисковому запросу и доступности
    const filteredEquipment = availableEquipment
        .filter((item) => {
            const matchesCategory = !selectedCategory || item.category_id === selectedCategory;

            // Убираем проверку доступности - теперь показываем всё оборудование, 
            // кроме того, что в обслуживании (maintenance)
            let isAvailable = item.status !== 'maintenance';

            // Отладочная информация для фильтрации
            if (!isAvailable && (matchesCategory)) {
                console.log('Оборудование отфильтровано как недоступное (в обслуживании):', {
                    id: item.id,
                    name: item.name,
                    is_available: item.is_available,
                    available_quantity: item.available_quantity,
                    status: item.status,
                    datesSelected: !!(selectedStartDate && selectedEndDate)
                });
            }

            return matchesCategory && isAvailable;
        })
        // Сортировка: сначала по категориям, затем внутри категории - доступное оборудование перед забронированным
        .sort((a, b) => {
            // Сначала сортируем по категориям
            if (a.category_id !== b.category_id) {
                const categoryA = categoriesData.find(cat => cat.id === a.category_id);
                const categoryB = categoriesData.find(cat => cat.id === b.category_id);

                // Если найдены обе категории и у них есть sort_order, сортируем по нему
                if (categoryA && categoryB && 'sort_order' in categoryA && 'sort_order' in categoryB) {
                    return (categoryA.sort_order as number) - (categoryB.sort_order as number);
                }

                // Иначе просто по ID категории
                return a.category_id.localeCompare(b.category_id);
            }

            // Если категории одинаковые, сначала сортируем по полю sort_order
            if (a.sort_order !== undefined && b.sort_order !== undefined) {
                // Преобразуем значения sort_order в числа, если они не null
                const sortOrderA = a.sort_order !== null ? Number(a.sort_order) : Number.MAX_SAFE_INTEGER;
                const sortOrderB = b.sort_order !== null ? Number(b.sort_order) : Number.MAX_SAFE_INTEGER;

                // Если sort_order разные, сортируем по ним
                if (sortOrderA !== sortOrderB) {
                    return sortOrderA - sortOrderB;
                }
            }

            // Если даты выбраны, учитываем статус оборудования (available/booked)
            if (selectedStartDate && selectedEndDate) {
                // 'available' должен быть перед 'booked'
                if (a.status === 'available' && b.status === 'booked') return -1;
                if (a.status === 'booked' && b.status === 'available') return 1;
            }

            // Если статусы одинаковые или даты не выбраны, сортируем по имени
            return a.name.localeCompare(b.name);
        });

    // Исправляем useEffect для отладочного логирования, добавляя проверку на availableEquipment
    useEffect(() => {
        if (selectedStartDate && selectedEndDate && availableEquipment.length > 0) {
            console.log('Filtered equipment:', filteredEquipment.length, 'items');
            console.log('Selected dates:', { startDate: selectedStartDate, endDate: selectedEndDate });
            console.log('Selected category:', selectedCategory);

            // Выводим значение is_available для каждого элемента оборудования
            console.log('Is available values:', availableEquipment.map(item => ({
                id: item.id,
                name: item.name,
                is_available: item.is_available,
                status: item.status,
                quantity: item.quantity,
                available_quantity: item.available_quantity
            })));
        }
    }, [filteredEquipment.length, selectedStartDate, selectedEndDate, selectedCategory, availableEquipment.length]);

    // Уникальные категории для фильтра (используем только те, которые есть у оборудования)
    const uniqueCategoryIds = [...new Set(equipment.map(item => item.category_id))];
    // Фильтруем категории, чтобы показывать только те, которые есть в списке оборудования
    const availableCategories = categoriesData.filter(cat => uniqueCategoryIds.includes(cat.id));

    // Обработчик выбора категории
    const handleCategoryChange = (categoryId: string | null) => {
        setSelectedCategory(categoryId);
    };

    // Обработчик выбора оборудования
    const handleEquipmentSelect = (equipment: Equipment | SelectedEquipment | null, quantity: number) => {
        if (!equipment) return;

        // Проверка, выбрано ли уже это оборудование
        const existingIndex = selectedEquipment.findIndex(item => item.id === equipment.id);

        if (existingIndex >= 0) {
            // Если уже выбрано, обновляем количество
            const updatedItems = [...selectedEquipment];

            if (quantity <= 0) {
                // Если количество <= 0, удаляем из списка
                updatedItems.splice(existingIndex, 1);
            } else {
                // Иначе обновляем количество
                updatedItems[existingIndex] = {
                    ...updatedItems[existingIndex],
                    quantity
                };
            }

            setSelectedEquipment(updatedItems);
        } else if (quantity > 0) {
            // Если еще не выбрано и количество > 0, добавляем в список
            // Преобразуем Equipment в SelectedEquipment при необходимости
            const selectedItem: SelectedEquipment = 'dailyRate' in equipment
                ? equipment as SelectedEquipment
                : {
                    id: equipment.id,
                    name: equipment.name,
                    quantity,
                    dailyRate: equipment.daily_rate,
                    imageUrl: equipment.image_url || undefined,
                    category_id: equipment.category_id,
                    status: equipment.status,
                    is_available: equipment.is_available,
                    available_quantity: equipment.available_quantity,
                    description: equipment.description,
                    sort_order: equipment.sort_order
                };

            setSelectedEquipment([
                ...selectedEquipment,
                {
                    ...selectedItem,
                    quantity
                }
            ]);
        }
    };

    // Обработчик нажатия кнопки бронирования
    const toggleBookingWidget = () => {
        // Проверяем авторизацию пользователя
        const currentUser = getCurrentUser();
        if (!currentUser) {
            // Если пользователь не авторизован, перенаправляем на страницу входа
            toast.dismiss();
            toast.error('Для бронирования необходимо авторизоваться');
            // Сохраняем URL текущей страницы для возврата после авторизации
            localStorage.setItem('redirectAfterLogin', window.location.pathname);
            // Перенаправляем на страницу входа
            navigate('/login');
            return;
        }

        if (!showBookingWidget) {
            // Если форма не отображается и нажали "Забронировать", показываем подтверждение
            if (selectedEquipment.length > 0 && selectedStartDate && selectedEndDate) {
                setShowBookingConfirmation(true);
            } else {
                // Если не выбрано оборудование или даты, показываем сообщение
                showToast("Пожалуйста, выберите оборудование и укажите даты аренды", "warning");
            }
        } else {
            // Если форма отображается, скрываем ее
            setShowBookingWidget(false);
        }
    };

    // Обработчик отмены бронирования
    const handleCancelBooking = () => {
        setShowBookingConfirmation(false);
    };

    // Обработчик успешного бронирования
    const handleBookingSuccess = (bookingId: string) => {
        setShowBookingConfirmation(false);

        // Очищаем выбранное оборудование
        setSelectedEquipment([]);

        // Показываем уведомление об успешном бронировании
        showToast(`Ваше бронирование #${bookingId.substring(0, 8)} создано.`, "success");

        // Перезагружаем страницу через небольшую задержку, чтобы пользователь увидел уведомление
        setTimeout(() => {
            window.location.reload();
        }, 1000);
    };

    // toast API
    const showToast = (message: string, type: 'success' | 'error' | 'warning') => {
        console.log(`Показываю уведомление: ${message} (${type})`); // Добавляем лог для отладки

        // Сначала закрываем все текущие уведомления
        toast.dismiss();

        // В react-hot-toast position может быть: 'top-left', 'top-center', 'top-right',
        // 'bottom-left', 'bottom-center', 'bottom-right' или просто 'center'
        if (type === 'success') {
            toast.success(message, {
                duration: 4000,
            });
        } else if (type === 'error') {
            toast.error(message, {
                duration: 4000,
            });
        } else {
            // Для предупреждений используем toast с особыми стилями
            toast(message, {
                duration: 4000,
                style: {
                    background: 'linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%)',
                    border: 'none',
                    color: '#9a3412',
                    borderRadius: '10px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    padding: '6px 10px',
                },
                icon: '⚠️',
            });
        }
    };

    // Создаем компонент Loader прямо в этом файле
    const Loader = () => (
        <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
    );

    // Функция для получения недоступных дат для конкретного оборудования
    const fetchDisabledDatesForEquipment = async (equipmentId: string) => {
        try {
            console.log('Получаем недоступные даты для оборудования ID:', equipmentId);

            // Используем новую функцию getEquipmentBlockedDates
            const { data: blockedDates, error } = await getEquipmentBlockedDates(equipmentId);

            if (error) {
                console.error('Ошибка при получении недоступных дат:', error);
                setDisabledDates([]);
                return;
            }

            console.log(`Получено ${blockedDates.length} недоступных дат для оборудования ${equipmentId}`);
            setDisabledDates(blockedDates);
        } catch (error) {
            console.error('Ошибка при получении недоступных дат:', error);
            setDisabledDates([]);
        }
    };

    // Обработчик выбора оборудования
    const handleSelectEquipment = (id: string) => {
        // Проверяем валидность ID
        if (!id || id.trim() === '') {
            console.error('Получен пустой ID');
            toast.error("Ошибка: невалидный идентификатор оборудования");
            return;
        }

        // Проверяем, выбрана ли уже эта карточка
        const isAlreadySelected = selectedEquipment.some(e => e.id === id);

        // Если карточка уже выбрана, удаляем её из выбранных
        if (isAlreadySelected) {
            console.log('Карточка уже выбрана, удаляем её из выбранных');
            const updatedSelectedEquipment = selectedEquipment.filter(e => e.id !== id);
            setSelectedEquipment(updatedSelectedEquipment);
            return;
        }

        console.log('Доступное оборудование:', availableEquipment.map(e => ({ id: e.id, name: e.name, idType: typeof e.id })));

        // Находим оборудование по id
        const equip = availableEquipment.find(e => e.id === id);

        console.log('Найдено оборудование:', equip);

        if (equip) {
            // Если даты не выбраны, открываем календарь и устанавливаем фокус на выбранном оборудовании
            if (!selectedStartDate || !selectedEndDate) {
                // Устанавливаем ID оборудования, для которого открываем календарь
                setCalendarFocusedEquipmentId(id);

                // Получаем недоступные даты для выбранного оборудования
                fetchDisabledDatesForEquipment(id);

                // Открываем календарь и подсвечиваем его
                setHighlightDateSelector(true);
                setShowCalendar(true);

                // Прокручиваем страницу к календарю, чтобы он был виден
                const calendarElement = document.querySelector('.date-range-selector');
                if (calendarElement) {
                    calendarElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }

                // Показываем уведомление о необходимости выбрать даты
                toast.dismiss();
                toast("Выберите даты аренды для этого оборудования", {
                    icon: '📅',
                    style: {
                        background: 'linear-gradient(135deg, #e9f5fd 0%, #d3eafd 100%)',
                        color: '#0369a1',
                        borderRadius: '10px',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                    },
                });

                return;
            }

            // Если даты выбраны, продолжаем стандартную логику выбора оборудования
            const maxQuantity = equip.available_quantity !== undefined
                ? equip.available_quantity
                : (equip.quantity || 0);

            if (maxQuantity === 1) {
                // Если доступна только 1 штука, сразу добавляем без модального окна
                console.log('Оборудование имеет всего 1 единицу, добавляем без модального окна');
                handleEquipmentSelect(equip, 1);
            } else {
                // Если больше 1, показываем модальное окно выбора количества
                setShowQuantityModal({ show: true, equipment: equip as unknown as Equipment });
            }
        } else {
            console.error('Оборудование с ID', id, 'не найдено в списке доступного оборудования');
            toast.error("Ошибка: не удалось найти выбранное оборудование");
        }
    };

    // Функция для расчета общей стоимости бронирования с учетом скидки
    const calculateTotalPriceWithDiscount = (
        equipmentItems: SelectedEquipment[], 
        startDate: Date | null, 
        endDate: Date | null, 
        discount: number
    ): number => {
        if (!startDate || !endDate || equipmentItems.length === 0) return 0;
        
        const days = Math.floor(
            (endDate.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24)
        ) || 1;
        
        // Базовая стоимость без скидки
        const originalPrice = equipmentItems.reduce((total: number, item: SelectedEquipment) => {
            return total + (item.dailyRate * item.quantity * days);
        }, 0);
        
        // Применяем скидку, если она есть
        return discount > 0 
            ? Math.round((originalPrice * (100 - discount)) / 100)
            : originalPrice;
    };

    return (
        <div className="max-w-6xl mx-auto px-4 py-4 bg-white">
            {/* Контейнер для выбора дат и категорий в одном ряду */}
            <div className="flex flex-wrap md:flex-nowrap gap-4 mb-5">
                {/* Виджет выбора дат - сокращенный, занимает половину ширины */}
                <div className="w-full md:w-1/2 flex">
                    <DateRangeSelector
                        onDatesChange={handleDatesChange}
                        highlightAnimation={highlightDateSelector}
                        className={`w-full flex-1 date-range-selector ${showCalendar ? 'calendar-opened' : ''}`}
                        forceOpen={showCalendar}
                        disabledDates={disabledDates}
                        focusedEquipmentName={calendarFocusedEquipmentId ? availableEquipment.find(e => e.id === calendarFocusedEquipmentId)?.name : undefined}
                    />
                </div>

                {/* Выпадающий список категорий вместо кнопок */}
                <div className="w-full md:w-1/2 flex">
                    <Dropdown
                        label="Категория"
                        items={availableCategories}
                        selectedId={selectedCategory}
                        onSelect={handleCategoryChange}
                        className="w-full flex-1"
                    />
                </div>
            </div>

            {/* Отображение количества выбранного оборудования и кнопки бронирования */}
            {selectedEquipment.length > 0 && (
                <div className="bg-white border border-primary-100 shadow-sm rounded-lg p-4 mb-5">
                    <div className="container mx-auto">
                        <div className="flex flex-col md:flex-row justify-between items-center gap-4 mb-3">
                            <div>
                                <p className="text-base font-medium text-gray-700 mb-1">Выбрано оборудования: {selectedEquipment.length} наименований</p>
                                {selectedStartDate && selectedEndDate && (
                                    <p className="text-sm text-yellow-600 mt-0">
                                        <span className="font-medium">Период аренды:</span> {selectedStartDate.toLocaleDateString('ru-RU')}
                                        {selectedStartDate.getTime() !== selectedEndDate.getTime() && ` — ${selectedEndDate.toLocaleDateString('ru-RU')}`}
                                        ({Math.floor((selectedEndDate.getTime() - selectedStartDate.getTime()) / (1000 * 60 * 60 * 24)) + 1 || 1} {getDaysText(Math.floor((selectedEndDate.getTime() - selectedStartDate.getTime()) / (1000 * 60 * 60 * 24)) + 1 || 1)})
                                    </p>
                                )}
                            </div>

                            <div className="flex gap-3 w-full md:w-auto">
                                <Button
                                    variant="outline"
                                    onClick={() => setSelectedEquipment([])}
                                    className="whitespace-nowrap"
                                >
                                    Очистить выбор
                                </Button>
                                <Button onClick={toggleBookingWidget} className="whitespace-nowrap">
                                    {showBookingWidget ? 'Скрыть форму' : 'Забронировать'}
                                </Button>
                            </div>
                        </div>

                        {/* Выбранное оборудование - перенесено из верхней панели */}
                        <div className="flex flex-wrap gap-2">
                            {selectedEquipment.map(item => (
                                <div
                                    key={item.id}
                                    className="bg-primary-50 rounded-full px-3 py-1 text-sm flex items-center border border-primary-100 text-gray-700"
                                >
                                    <span className="mr-2">{item.name} ({item.quantity} шт.)</span>
                                    <button
                                        onClick={() => handleEquipmentSelect(item, 0)}
                                        className="text-gray-500 hover:text-red-500"
                                    >
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                        </svg>
                                    </button>
                                </div>
                            ))}
                        </div>

                        {/* Итоговая сумма бронирования */}
                        {selectedStartDate && selectedEndDate && (
                            <div className="mt-3 pt-3 border-t border-gray-200">
                                <div className="flex justify-between items-center">
                                    <span className="text-sm text-gray-600">Итоговая сумма:</span>
                                    <div className="text-right">
                                        {userDiscount > 0 && (
                                            <div className="flex items-center mb-1">
                                                <span className="text-sm text-gray-600 mr-2">Применить скидку {userDiscount}%:</span>
                                                <label className="relative inline-flex items-center cursor-pointer">
                                                    <input 
                                                        type="checkbox" 
                                                        checked={applyDiscount} 
                                                        onChange={() => setApplyDiscount(!applyDiscount)} 
                                                        className="sr-only peer"
                                                    />
                                                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-yellow-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
                                                </label>
                                            </div>
                                        )}
                                        {userDiscount > 0 && applyDiscount && (
                                            <div className="text-sm text-green-600 mb-1">
                                                Скидка: {userDiscount}%
                                            </div>
                                        )}
                                        <span className="text-lg font-semibold text-yellow-600">
                                            {calculateTotalPriceWithDiscount(
                                                selectedEquipment,
                                                selectedStartDate,
                                                selectedEndDate,
                                                applyDiscount ? userDiscount : 0
                                            ).toLocaleString('ru-RU')} ₽
                                        </span>
                                    </div>
                                </div>
                                <div className="text-xs text-gray-500 text-right mt-1">
                                    за весь период аренды
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Модальное окно подтверждения бронирования */}
            {showBookingConfirmation && selectedStartDate && selectedEndDate && (
                <BookingConfirmation
                    selectedEquipment={selectedEquipment}
                    startDate={selectedStartDate}
                    endDate={selectedEndDate}
                    onCancel={handleCancelBooking}
                    onSuccess={handleBookingSuccess}
                    applyDiscount={applyDiscount}
                />
            )}

            {/* Статус загрузки */}
            {isLoadingCombined && <Loader />}

            {/* Сообщение об ошибке */}
            {isError && (
                <div className="bg-red-50 text-red-700 p-4 rounded-md my-6">
                    Произошла ошибка при загрузке данных. Пожалуйста, попробуйте позже.
                </div>
            )}

            {/* Сообщение, если нет оборудования */}
            {!isLoadingCombined && !isError && filteredEquipment.length === 0 && (
                <div className="bg-gray-50 p-8 rounded-md text-center my-6">
                    <h3 className="text-lg font-medium text-gray-700 mb-2">Оборудование не найдено</h3>
                    <p className="text-gray-500">
                        {selectedCategory
                            ? `В выбранной категории "${selectedCategory}" нет доступного оборудования.`
                            : 'В выбранной категории нет доступного оборудования.'}
                    </p>
                </div>
            )}

            {/* Список оборудования */}
            <div className="flex flex-wrap -mx-2">
                {filteredEquipment.length > 0 ? (
                    filteredEquipment.map((item) => (
                        <EquipmentCard
                            key={item.id}
                            id={item.id}
                            name={item.name}
                            price={item.dailyRate}
                            image_url={item.imageUrl || ''}
                            status={item.status}
                            quantity={item.quantity}
                            availableQuantity={item.available_quantity}
                            onSelect={handleSelectEquipment}
                            selected={selectedEquipment.some(e => e.id === item.id)}
                            datesSelected={!!(selectedStartDate && selectedEndDate)}
                            userDiscount={applyDiscount ? userDiscount : 0}
                        />
                    ))
                ) : (
                    <div className="w-full text-center py-8">
                        <p className="text-gray-500">
                            {isLoadingCombined
                                ? 'Загрузка оборудования...'
                                : 'Оборудование не найдено. Попробуйте изменить параметры поиска.'}
                        </p>
                    </div>
                )}
            </div>

            {/* Модальное окно выбора количества */}
            {showQuantityModal.show && showQuantityModal.equipment && (
                <QuantityModal
                    equipment={showQuantityModal.equipment}
                    onConfirm={(id, quantity) => {
                        // Находим оборудование в списке доступного
                        const equip = availableEquipment.find(e => e.id === id);
                        if (equip) {
                            handleEquipmentSelect(equip, quantity);
                        }
                        setShowQuantityModal({ show: false, equipment: null });
                    }}
                    onCancel={() => setShowQuantityModal({ show: false, equipment: null })}
                />
            )}
        </div>
    );
};

export default EquipmentPage;
